import * as React from "react";
import { Form, useFormApi } from "informed";
import { TextBox } from "../../../../forms/informedv2/TextBox";
import { TextBoxWithStaticPrefix } from "../../../../forms/informedv2/TextBoxWithStaticPrefix";
import LoadingWithMessage from "../../../../misc/components/LoadingWithMessage";
import { SubjectFieldValidator } from "../../../../forms/validation/ConfigurableItemValidator";
import { DataItemConfiguration } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../../Core/CurrentStudyConfigurationContext";
import { ConfigurableDataItemIds } from "../../../../misc/api/ApiConfigurableDataItems";
import { nameof } from "ts-simple-nameof";

interface EnterNewSubjectIdProps {
    originalSubjectId: string;
    correctedSubjectId: string;
    onNext: (newSubjectId: string) => void;
    siteNumber: string;
}


interface EnterNewSubjectIdFormProps {
    onNext: (newSubjectId: string) => void;
    siteNumber: string;
    fieldConfig: DataItemConfiguration;
}

interface EnterNewSubjectIdFormState {
    originalSubjectId: string;
    newSubjectId: string;
}

const EnterNewSubjectIdForm = ({ onNext, siteNumber, fieldConfig }: EnterNewSubjectIdFormProps): JSX.Element => {
    const subjectFieldValidator = fieldConfig ? SubjectFieldValidator(fieldConfig) : () => undefined;
    const formApi = useFormApi<EnterNewSubjectIdFormState>();

    const getInput = (fieldId: string, disabled: boolean) => {
        return fieldConfig.prefixWithSiteNumber ? <TextBoxWithStaticPrefix id={fieldId} type="text" field={fieldId} validate={subjectFieldValidator} disabled={disabled} staticpart={siteNumber} /> : <TextBox id={fieldId} type="text" field={fieldId} validate={subjectFieldValidator} disabled={disabled} />;
    }

    const getNewSubjectId = (subjectId: string): string => {
        return fieldConfig.prefixWithSiteNumber ? siteNumber + subjectId : subjectId;
    }

    const onSubmit = () => {
        formApi.validate();

        const state = formApi.getState();

        onNext(getNewSubjectId(state.values.newSubjectId));
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <p className="text-muted">
                            Correcting {fieldConfig.displayText}.
                        </p>
                        <div className="form-group">
                            <label>Current {fieldConfig.displayText}</label>&nbsp;
                            {getInput(nameof<EnterNewSubjectIdFormState>(f => f.originalSubjectId), true)}
                        </div>
                        <div className="form-group">
                            <label>Enter New {fieldConfig.displayText}</label>&nbsp;
                            {getInput(nameof<EnterNewSubjectIdFormState>(f => f.newSubjectId), false)}
                        </div>

                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => onSubmit()}>Next</button>
            </div>
        </>);
};

export const EnterNewSubjectId = ({ originalSubjectId, correctedSubjectId, onNext, siteNumber }: EnterNewSubjectIdProps) => {
    const [fieldConfig, setFieldConfig] = React.useState<DataItemConfiguration>(null);
    const [initialValues, setInitialValues] = React.useState<EnterNewSubjectIdFormState>(null);
    const { dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const config = dataItemConfigurationCollection.find(dc => dc.configurableDataItemId === ConfigurableDataItemIds.SubjectId);
        setFieldConfig(config);

        setInitialValues({
            originalSubjectId: removeSitePrefix(originalSubjectId, config),
            newSubjectId: removeSitePrefix(correctedSubjectId, config),
        });
    }, [dataItemConfigurationCollection]);

    const removeSitePrefix = (value: string, config: DataItemConfiguration): string => {
        if (config) {
            return config.prefixWithSiteNumber ? value.replace(new RegExp("^" + siteNumber), "") : value;
        }
    }

    if (!fieldConfig || !initialValues)
        return <LoadingWithMessage message="Loading Field Configuration" />;

    return (
        <Form initialValues={initialValues}>
            <EnterNewSubjectIdForm onNext={onNext} siteNumber={siteNumber} fieldConfig={fieldConfig} />
        </Form>
    );
}
